import type { LeagueTeamName } from './teamNames'

/**
 * Additional scheduled game in a series (e.g. same-player rematch after a draft round).
 */
export interface PlayoffExtraMatchup {
  id: string
  insertAfterRound: number
  label: string
  homeUsername: string
  awayUsername: string
}

/**
 * Team-series pairings for a playoff bracket round (one game per draft round per player).
 */
export interface PlayoffSeriesPairing {
  id: string
  title: string
  teamA: LeagueTeamName
  teamB: LeagueTeamName
  playoffRound: 1 | 2 | 3
  homeTeamByRound?: Partial<Record<number, LeagueTeamName>>
  /** Preferred home competitor username for each draft round (falls back to {@link homeTeamByRound}). */
  homeUserByRound?: Partial<Record<number, string>>
  /** Extra games spliced into the schedule after a draft round (same players may appear twice). */
  extraMatchups?: PlayoffExtraMatchup[]
}

/**
 * Opening playoff round — three team series.
 */
export const PLAYOFF_ROUND_1_SERIES: PlayoffSeriesPairing[] = [
  {
    id: 'nos-ac-slater',
    title: 'Team Nos vs Team AC Slater',
    teamA: 'Team Nos',
    teamB: 'Team AC Slater',
    playoffRound: 1,
    homeTeamByRound: {
      1: 'Team AC Slater',
      2: 'Team Nos',
      3: 'Team AC Slater',
      4: 'Team Nos',
      5: 'Team Nos',
      6: 'Team AC Slater',
    },
  },
  {
    id: 'dps-nostradamus',
    title: 'Team DPS vs Team Nostradamus',
    teamA: 'Team DPS',
    teamB: 'Team Nostradamus',
    playoffRound: 1,
    homeTeamByRound: {
      1: 'Team Nostradamus',
      2: 'Team Nostradamus',
      3: 'Team Nostradamus',
      4: 'Team DPS',
      5: 'Team DPS',
      6: 'Team DPS',
    },
  },
  {
    id: 'vikingmoe-bruddog',
    title: 'Team VikingMoe vs Team Bruddog',
    teamA: 'Team VikingMoe',
    teamB: 'Team Bruddog',
    playoffRound: 1,
    homeTeamByRound: {
      1: 'Team Bruddog',
      2: 'Team VikingMoe',
      3: 'Team Bruddog',
      4: 'Team VikingMoe',
      5: 'Team VikingMoe',
      6: 'Team Bruddog',
    },
  },
]

/**
 * Semifinal team series (Round 2).
 */
export const PLAYOFF_ROUND_2_SERIES: PlayoffSeriesPairing[] = [
  {
    id: 'semifinal-regs-bruddog',
    title: 'Team Regulator vs Team Bruddog',
    teamA: 'Team Regulator',
    teamB: 'Team Bruddog',
    playoffRound: 2,
    homeUserByRound: {
      1: 'regulator088',
      2: 'randywags25',
      3: 'cmoney2081',
      4: 'protajay.',
      5: 'realbeefjuice',
      6: 'edboi420',
    },
    homeTeamByRound: {
      1: 'Team Regulator',
      2: 'Team Bruddog',
      3: 'Team Regulator',
      4: 'Team Regulator',
      5: 'Team Bruddog',
      6: 'Team Bruddog',
    },
  },
  {
    id: 'semifinal-dps-acslater',
    title: 'Team DPS vs Team AC Slater',
    teamA: 'Team DPS',
    teamB: 'Team AC Slater',
    playoffRound: 2,
    homeUserByRound: {
      1: 'tecmodps',
      2: 'honortogod',
      3: 'swampdaddy',
      4: 'nel00',
      5: 'ag83',
      6: 'richiesmith',
    },
    homeTeamByRound: {
      1: 'Team DPS',
      2: 'Team DPS',
      3: 'Team AC Slater',
      4: 'Team DPS',
      5: 'Team AC Slater',
      6: 'Team AC Slater',
    },
    extraMatchups: [
      {
        id: 'r1-dps-acslater-rematch',
        insertAfterRound: 1,
        label: 'Rematch',
        homeUsername: 'tecmodps',
        awayUsername: 'acslatertecmo',
      },
    ],
  },
]

/**
 * Championship series (final round).
 */
export const PLAYOFF_ROUND_3_SERIES: PlayoffSeriesPairing[] = [
  {
    id: 'championship-regs-dps',
    title: 'Team Regulator vs Team DPS',
    teamA: 'Team Regulator',
    teamB: 'Team DPS',
    playoffRound: 3,
    homeUserByRound: {
      1: 'regulator088',
      2: 'honortogod',
      3: 'cmoney2081',
      4: 'nel00',
      5: 'bigred17machine',
      6: '_uyio_',
    },
    homeTeamByRound: {
      1: 'Team Regulator',
      2: 'Team DPS',
      3: 'Team Regulator',
      4: 'Team DPS',
      5: 'Team Regulator',
      6: 'Team DPS',
    },
  },
]

/** Tournament champion (set when the final series is decided). */
export const PLAYOFF_CHAMPION_TEAM: LeagueTeamName = 'Team DPS'

/** @deprecated Use {@link PLAYOFF_ROUND_1_SERIES}. */
export const PLAYOFF_SERIES_PAIRINGS = PLAYOFF_ROUND_1_SERIES

/** All scheduled playoff team series in bracket order. */
export const ALL_PLAYOFF_SERIES = [
  ...PLAYOFF_ROUND_1_SERIES,
  ...PLAYOFF_ROUND_2_SERIES,
  ...PLAYOFF_ROUND_3_SERIES,
]

/**
 * Semifinal team pairings for the bracket page (derived from Round 2).
 */
export const SEMIFINAL_BRACKET_MATCHUPS = PLAYOFF_ROUND_2_SERIES.map(series => ({
  id: series.id,
  teamA: series.teamA,
  teamB: series.teamB,
}))

/**
 * Number of draft rounds represented by one playoff game per player.
 */
export const PLAYOFF_DRAFT_ROUNDS = [1, 2, 3, 4, 5, 6] as const

/**
 * Games uploaded after this instant count as playoff results when only one head-to-head
 * row exists between players who were also scheduled in group play.
 */
export const PLAYOFF_RESULTS_START_ISO = '2026-05-14T00:00:00.000Z'

/**
 * Playoff schedule sections shown on the Playoffs page.
 */
export const PLAYOFF_PAGE_ROUNDS = [
  { playoffRound: 1 as const, heading: 'Round 1' },
  { playoffRound: 2 as const, heading: 'Round 2' },
  { playoffRound: 3 as const, heading: 'Championship' },
] as const
